import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MapComponent } from "../../map/map";
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts';
import 'echarts-gl';
import { ShipScheduleService } from '../ship-schedule/ship-schedule.service';
import { ApiService } from '../../apiService/api-service';

interface WidgetData {
  id: string;
  icon: string;
  label: string;
  val: number | undefined;
  unit: string;
  threshold?: { has: boolean; val: number };
  pct: number;
  barColor: string;
  trend?: number[];
  lat?: number;
  lon?: number;
  altitude?: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, MapComponent, NgxEchartsDirective],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
  providers: [
    provideEchartsCore({ echarts })
  ]
})
export class Dashboard implements OnInit, OnDestroy, OnChanges {
  @Input() anemometerData: any;
  @Input() selectedStation: any | null = null;
  @Input() widgetData: WidgetData[] = [];
  @Input() fullDayTrends: { [key: string]: any[] } = {};
  @Input() isDarkMode: boolean = true;
  @Input() lastDataTimestamp: number = 0;

  currentSlide: number = 1;
  private slideInterval: any;
  private extensions = ['webp', 'jpeg', 'jpg', 'png'];
  private currentExtIndex = 0;
  private watchdogInterval: any;

  // Stable options for 3D chart to prevent re-initialization on every update
  threeDOptions: any;
  threeDUpdate: any;

  routeCoordinates: [number, number][] = [];
  actualPath: [number, number][] = [];
  currentScheduleName: string = '';
  activeCruise: any = null;

  constructor(private cdr: ChangeDetectorRef, private scheduleService: ShipScheduleService, private apiService: ApiService) { }

  get currentImagePath() {
    return `assets/${this.currentSlide}.${this.extensions[this.currentExtIndex]}`;
  }

  onImageError() {
    if (this.currentExtIndex < this.extensions.length - 1) {
      this.currentExtIndex++;
    } else {
      console.warn(`All extensions failed for slide ${this.currentSlide}`);
    }
  }

  getNormalCards() {
    return this.widgetData.filter(w => w.id !== 'gps' && w.id !== 'battery' && w.id !== 'wind_speed' && w.id !== 'wind_direction' && w.id !== 'sos');
  }

  getGpsCard() {
    return this.widgetData.find(w => w.id === 'gps');
  }

  getBatteryCard() {
    return this.widgetData.find(w => w.id === 'battery');
  }

  getWindSpeedCard() {
    return this.widgetData.find(w => w.id === 'wind_speed');
  }

  getWindDirectionCard() {
    return this.widgetData.find(w => w.id === 'wind_direction');
  }

  getWidgetById(id: string) {
    return this.widgetData.find(w => w.id === id);
  }

  getMin(trend: number[] | undefined): number {
    if (!trend || trend.length === 0) return 0;
    return Math.min(...trend);
  }

  getMax(trend: number[] | undefined): number {
    if (!trend || trend.length === 0) return 0;
    return Math.max(...trend);
  }

  isExceedingThreshold(w: WidgetData): boolean {
    if (!w || !w.threshold?.has || w.val === undefined) return false;
    return Number(w.val) > w.threshold.val;
  }

  abs(val: number | undefined): number {
    return Math.abs(val || 0);
  }

  getWindDirectionCardinal(degrees: number | undefined): string {
    if (degrees === undefined) return '';
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(((degrees %= 360) < 0 ? degrees + 360 : degrees) / 45) % 8;
    return directions[index];
  }

  getChartOption(trend: number[] | undefined, isDarkMode: boolean, colorClass: string, id: string): echarts.EChartsOption {
    let rawTrend = trend || [];
    let displayTrend = rawTrend;

    let color = '#3b82f6';
    if (colorClass.includes('cyan')) color = '#22d3ee';
    else if (colorClass.includes('emerald') || colorClass.includes('green')) color = '#10b981';
    else if (colorClass.includes('orange') || colorClass.includes('yellow')) color = '#f97316';
    else if (colorClass.includes('indigo')) color = '#6366f1';

    const widget = this.widgetData.find(w => w.id === id);
    const threshold = widget?.threshold;

    return {
      grid: { top: 20, bottom: -5, left: -5, right: -5 },
      xAxis: { type: 'category', show: false, boundaryGap: false },
      yAxis: { type: 'value', show: false, min: 'dataMin', max: 'dataMax' },
      visualMap: threshold?.has ? {
        show: false,
        pieces: [{ gt: 0, lte: threshold.val, color: color }, { gt: threshold.val, color: '#ef4444' }],
        outOfRange: { color: color }
      } : undefined,
      series: [{
        data: displayTrend,
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          color: threshold?.has ? undefined : color,
          width: 2,
          shadowColor: isDarkMode ? color : 'transparent',
          shadowOffsetY: 0
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: isDarkMode ? 'transparent' : `${color}30` },
            { offset: 1, color: isDarkMode ? 'transparent' : `${color}00` }
          ])
        }
      }]
    };
  }

  update3DChart(): void {
    const uTrend = this.fullDayTrends['u'] || [];
    const vTrend = this.fullDayTrends['v'] || [];
    const wTrend = this.fullDayTrends['w'] || [];

    const data3d = uTrend.map((uPoint, i) => {
      const vPoint = vTrend[i];
      const wPoint = wTrend[i];
      if (!uPoint || !vPoint || !wPoint) return null;
      return [uPoint[1], vPoint[1], wPoint[1]];
    }).filter(p => p !== null);

    this.threeDUpdate = {
      series: [{
        data: data3d
      }]
    };
    this.cdr.markForCheck();
  }

  get3DChartBaseOptions(isDarkMode: boolean): any {
    const textColor = isDarkMode ? '#f8fafc' : '#1e293b';
    const labelColor = isDarkMode ? '#94a3b8' : '#64748b';
    const gridColor = isDarkMode ? 'rgba(155, 155, 155, 0.1)' : 'rgba(0, 0, 0, 0.05)';

    return {
      tooltip: {
        backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        borderColor: '#22d3ee',
        textStyle: { color: textColor },
        formatter: (params: any) => {
          const val = params.value;
          return `U: ${val[0].toFixed(2)}<br/>V: ${val[1].toFixed(2)}<br/>W: ${val[2].toFixed(2)}`;
        }
      },
      xAxis3D: {
        name: 'U', type: 'value',
        nameTextStyle: { color: '#22d3ee', fontSize: 13, fontWeight: 'bold' },
        axisLabel: { textStyle: { color: labelColor, fontSize: 10 } },
        splitLine: { lineStyle: { color: gridColor, width: 1 } },
        splitNumber: 2
      },
      yAxis3D: {
        name: 'V', type: 'value',
        nameTextStyle: { color: '#22d3ee', fontSize: 13, fontWeight: 'bold' },
        axisLabel: { textStyle: { color: labelColor, fontSize: 10 } },
        splitLine: { lineStyle: { color: gridColor, width: 1 } },
        splitNumber: 2
      },
      zAxis3D: {
        name: 'W', type: 'value',
        nameTextStyle: { color: '#fbbf24', fontSize: 13, fontWeight: 'bold' },
        axisLabel: { textStyle: { color: labelColor, fontSize: 10 } },
        splitLine: { lineStyle: { color: gridColor, width: 1 } },
        splitNumber: 2
      },
      grid3D: {
        viewControl: {
          autoRotate: false,
          beta: 25,
          alpha: 20,
          distance: 180,
          panSensitivity: 1,
          rotateSensitivity: 1,
          zoomSensitivity: 1
        },
        boxWidth: 100, boxDepth: 100, boxHeight: 80,
        axisPointer: { show: true, lineStyle: { color: '#22d3ee', width: 2 } },
        light: {
          main: { intensity: 1.5, shadow: false },
          ambient: { intensity: 1.0 } 
        }
      },
      series: [{
        type: 'scatter3D',
        data: [],
        symbolSize: 8,
        itemStyle: {
          opacity: 0.9,
          color: '#22d3ee',
          shadowBlur: 10,
          shadowColor: 'rgba(34, 211, 238, 0.5)'
        },
        shading: 'lambert',
        emphasis: {
          itemStyle: { color: '#ffffff', symbolSize: 12 }
        }
      }]
    };
  }

  getSensorStatuses() {
    const now = new Date();
    const isOnline = (id: string, group: string[]) => {
      const timeDiff = now.getTime() - this.lastDataTimestamp;
      if (timeDiff > 60000) return 'offline'; // 1 minute timeout

      // Check if any of the group parameters have valid data
      if (this.anemometerData && group.length > 0) {
        const hasData = group.some(key => {
          const val = this.anemometerData[key];
          return val !== null && val !== undefined && val !== 0; // Use 0 check if appropriate, but null/undefined is safer
        });
        if (!hasData) return 'offline';
      }
      return 'online';
    };

    // Use lastDataTimestamp for everything as requested
    const displayTime = new Date(this.lastDataTimestamp);
    const day = String(displayTime.getDate()).padStart(2, '0');
    const month = String(displayTime.getMonth() + 1).padStart(2, '0');
    const year = displayTime.getFullYear();
    const time = displayTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    const timestamp = `${day}/${month}/${year} ${time}`;

    return [
      { id: 'anemometer', label: 'Anemometer', icon: 'fa-wind', status: isOnline('anemometer', ['u', 'v', 'w', 'wind_speed', 'wind_direction']), time: timestamp },
      { id: 'precipitation', label: 'Precipitation', icon: 'fa-cloud-showers-heavy', status: isOnline('rain', ['rain']), time: timestamp },
      { id: 'pyranometer', label: 'Pyranometer', icon: 'fa-sun', status: isOnline('solar', ['solar']), time: timestamp },
      { id: 'barometric', label: 'Barometric', icon: 'fa-gauge-high', status: isOnline('pressure', ['pressure']), time: timestamp },
      { id: 'temphum', label: 'Temp & Humidity', icon: 'fa-temperature-half', status: isOnline('temp', ['temp', 'humidity']), time: timestamp },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['anemometerData'] && changes['anemometerData'].currentValue) {
      if (!this.currentScheduleName) {
        this.setActualPathToLive();
      } else {
        if (this.actualPath.length > 0) {
          const last = this.actualPath[this.actualPath.length - 1];
          // Only update if changed visually
          if (last[0] !== this.anemometerData.lon || last[1] !== this.anemometerData.lat) {
            this.actualPath = [...this.actualPath, [this.anemometerData.lon, this.anemometerData.lat]];
          }
        } else {
          this.setActualPathToLive();
        }
      }
    }
    if (changes['fullDayTrends'] || changes['isDarkMode']) {
      if (!this.threeDOptions || changes['isDarkMode']) {
        this.threeDOptions = this.get3DChartBaseOptions(this.isDarkMode);
      }
      this.update3DChart();
    }
    // Refresh schedule/route if station changes
    if (changes['selectedStation'] && !changes['selectedStation'].firstChange) {
      this.refreshActiveCruise();
    }
  }

  ngOnInit(): void {
    // Initial 3D chart setup
    this.threeDOptions = this.get3DChartBaseOptions(this.isDarkMode);
    this.update3DChart();

    this.refreshActiveCruise();

    this.slideInterval = setInterval(() => {
      this.currentSlide = this.currentSlide === 3 ? 1 : this.currentSlide + 1;
      this.currentExtIndex = 0;
    }, 3000);

    this.watchdogInterval = setInterval(() => {
      this.cdr.markForCheck();
    }, 1000);
  }

  refreshActiveCruise() {
    // Fetch current schedule and determine active cruise
    this.scheduleService.getCruises().subscribe({
      next: (cruises) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const activeCruise = cruises.find(c => {
          let sDate = new Date(c.startDate);
          let eDate = new Date(c.endDate);
          sDate.setHours(0, 0, 0, 0);
          eDate.setHours(23, 59, 59, 999);
          return today >= sDate && today <= eDate;
        });

        this.activeCruise = activeCruise;

        if (activeCruise && activeCruise.stations) {
          this.currentScheduleName = activeCruise.project || activeCruise.vessel || activeCruise.id;
          const validStations = activeCruise.stations.filter((s: any) => s.latitude && s.longitude);
          this.routeCoordinates = validStations.map((s: any) => [s.longitude, s.latitude] as [number, number]);

          // Fetch historical ship positions from the start of the schedule until now
          const sDateStr = this.apiService.formatDateForQuery(new Date(activeCruise.startDate));
          const eDateStr = this.apiService.formatDateForQuery(new Date());

          const tableName = this.selectedStation?.table_name || 'tb_wind';
          this.apiService.getFilteredWindData(sDateStr, eDateStr, undefined, undefined, tableName).then(historical => {
            if (historical && historical.length > 0) {
              this.actualPath = historical
                .filter(h => h.lat && h.lon)
                .map(h => [h.lon, h.lat] as [number, number])
                .reverse();
            } else {
              this.setActualPathToLive();
            }
            this.cdr.markForCheck();
          });
        } else {
          this.currentScheduleName = '';
          this.routeCoordinates = [];
          this.setActualPathToLive();
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Failed to load cruises', err);
        this.setActualPathToLive();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.slideInterval) clearInterval(this.slideInterval);
    if (this.watchdogInterval) clearInterval(this.watchdogInterval);
  }

  setActualPathToLive() {
    if (this.anemometerData && this.anemometerData.lat && this.anemometerData.lon) {
      this.actualPath = [[this.anemometerData.lon, this.anemometerData.lat]];
    } else {
      this.actualPath = [];
    }
  }
}
